import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { controlBatch, planBatch, previewScope, readBatch, type HumanStepHandling, type ScopePreview } from "../../api/threads/batchesApi";
import type { ThreadSettings } from "../../api/threads/liveMessages";
import { listRouteOptions } from "../../api/threads/routeOptions";
import { newClientKey } from "../../api/threads/threadsApi";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { useSubmission } from "../../shared/state/useSubmission";
import LlmPicker from "../threads/llmChoice/LlmPicker";
import { withoutEffort } from "../threads/llmChoice/llmOptions";
import { useLlmChoices } from "../threads/llmChoice/useLlmChoices";
import { optionOf, settingsWithChoice, type TurnRoute } from "../threads/hosting/routeChoices";
import { batchRouteChoices, suggestedBatchRoute } from "./batchRoutes";
import AppButton from "../../ui/buttons/AppButton";
import ModalShell from "../../ui/overlays/ModalShell";
import { FORM_CONTROL_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../ui/styles/formStyles";
import { scopeOf, type DocumentChoice, type ScopeForm } from "./scopeForm";
import FieldGroup from "../../ui/forms/FieldGroup";
import Field from "../../ui/forms/Field";

/** What a batch takes from the Thread it was vetted in (UC-8): the same Workflow version,
 * model version, corpus version, route and profile, on the same connector. */
export interface BatchSource {
  readonly projectId: string;
  readonly threadId: string;
  readonly title: string;
  readonly workflow: string;
  readonly contentVersion: string;
  readonly corpusVersion: string;
  readonly threadSettings: ThreadSettings;
  readonly connectorId: string;
}

type DocumentKind = DocumentChoice["kind"];
type Grouping = "per_item" | "chunked";
const PREVIEW_SHOWN = 8;

/**
 * "Run on many documents" (FR-WF-5): the Workflow of a vetted Thread over the documents
 * or segments of a scope, one child Thread each, on the host's own connector (C-3).
 */
export default function StartBatchDialog({ source, onClose }: { source: BatchSource; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [documentKind, setDocumentKind] = useState<DocumentKind>("corpus");
  const [keysText, setKeysText] = useState("");
  const [field, setField] = useState("");
  const [value, setValue] = useState("");
  const [segmentType, setSegmentType] = useState("");
  const [title, setTitle] = useState(`${source.title} · ${t("batches.start.many")}`.slice(0, 200));
  const [concurrency, setConcurrency] = useState("");
  const [humanSteps, setHumanSteps] = useState<HumanStepHandling>("queue");
  const [grouping, setGrouping] = useState<Grouping>("per_item");
  const [chunkSize, setChunkSize] = useState("10");
  const [preview, setPreview] = useState<ScopePreview | null>(null);
  // One key per plan: changing the scope or settings plans a new batch; submitting the same
  // plan again (after a lost answer) finds the batch planned before and starts it once.
  const [clientRequestId, setClientRequestId] = useState(newClientKey);
  const [plannedBatchId, setPlannedBatchId] = useState<string | null>(null);
  // The LLM is chosen in the one LLM picker (W-07, F106); the batch suggests an API-key route
  // first (batchRoutes), which the picker shows chosen until the person picks another.
  const optionsOf = useCallback((connectorId: string, signal: AbortSignal) => listRouteOptions(source.threadId, connectorId, signal), [source.threadId]);
  const { state: llmChoices, refresh } = useLlmChoices([source.connectorId], optionsOf);
  const listing = llmChoices.status === "loaded" ? llmChoices.value[0]?.listing : undefined;
  const suggestions = listing === undefined ? [] : batchRouteChoices(listing.options);
  const [chosenRoute, setChosenRoute] = useState<TurnRoute | null>(null);
  const routeChoice = chosenRoute ?? suggestions.find((choice) => choice.key === suggestedBatchRoute(suggestions, source.threadSettings.route))?.route ?? null;
  const previewing = useSubmission();
  const starting = useSubmission();

  const documents: DocumentChoice =
    documentKind === "corpus" ? { kind: "corpus" } : documentKind === "documents" ? { kind: "documents", keysText } : { kind: "metadata", field, value };
  const form: ScopeForm = { documents, segmentType };
  const chosen = scopeOf(form);
  const parsedConcurrency = concurrency.trim() === "" ? null : Number.parseInt(concurrency, 10);
  const parsedChunk = Number.parseInt(chunkSize, 10);
  const settingsValid = (parsedConcurrency === null || parsedConcurrency >= 1) && (grouping === "per_item" || parsedChunk >= 1);
  const busy = previewing.state.status === "submitting" || starting.state.status === "submitting";

  const changed = (update: () => void) => {
    update();
    setPreview(null);
    replan();
  };
  const replan = () => {
    setPlannedBatchId(null);
    setClientRequestId(newClientKey());
  };

  const showPreview = () => {
    if (!("scope" in chosen)) return;
    previewing.submit(async () => {
      setPreview(await previewScope(source.projectId, source.corpusVersion, chosen.scope));
    });
  };

  const start = () => {
    if (!("scope" in chosen) || !settingsValid) return;
    starting.submit(async () => {
      const batchId = plannedBatchId ?? (await plan());
      setPlannedBatchId(batchId);
      if ((await readBatch(batchId)).state === "planned") {
        await controlBatch(batchId, "start");
      }
      onClose();
      void navigate(appPaths.batch(batchId));
    });
  };

  const plan = async (): Promise<string> => {
    if (!("scope" in chosen)) throw new Error("A batch needs a scope.");
    const planned = await planBatch({
        projectId: source.projectId,
        title: title.trim() === "" ? source.title : title.trim(),
        workflow: source.workflow,
        contentVersion: source.contentVersion,
        corpusVersion: source.corpusVersion,
        scope: chosen.scope,
        settings: {
          concurrency: parsedConcurrency,
          human_steps: humanSteps,
          change_sets: grouping === "per_item" ? { mode: "per_item" } : { mode: "chunked", items_per_change_set: parsedChunk },
        },
        threadSettings: routeChoice === null ? source.threadSettings : (settingsWithChoice(source.threadSettings, { connectorId: source.connectorId, route: routeChoice, permissionProfile: source.threadSettings.permission_profile, fallbackRoutes: source.threadSettings.fallback_routes ?? [], dropsDefaultReplyTools: optionOf(listing?.options ?? null, withoutEffort(routeChoice))?.dropsDefaultReplyTools ?? false }) ?? source.threadSettings),
        connectorId: source.connectorId,
        clientRequestId,
      });
    return planned.batchId;
  };

  return (
    <ModalShell title={t("batches.start.title")} subtitle={t("batches.start.subtitle")} onClose={onClose} size="xl">
      <div className="space-y-4 text-sm">
        <FieldGroup legend={t("batches.start.documents")} className="space-y-2">
          <select className={FORM_SELECT_CLASS} value={documentKind} onChange={(event) => { changed(() => { setDocumentKind(event.target.value as DocumentKind); }); }}>
            <option value="corpus">{t("batches.start.wholeCorpus")}</option>
            <option value="documents">{t("batches.start.chosenDocuments")}</option>
            <option value="metadata">{t("batches.start.metadata")}</option>
          </select>
          {documentKind === "documents" ? (
            <textarea className={FORM_CONTROL_CLASS} rows={3} value={keysText} placeholder={t("batches.start.keysPlaceholder")} onChange={(event) => { changed(() => { setKeysText(event.target.value); }); }} />
          ) : null}
          {documentKind === "metadata" ? (
            <div className="flex gap-2">
              <input className={FORM_CONTROL_CLASS} value={field} placeholder={t("batches.start.field")} onChange={(event) => { changed(() => { setField(event.target.value); }); }} />
              <input className={FORM_CONTROL_CLASS} value={value} placeholder={t("batches.start.value")} onChange={(event) => { changed(() => { setValue(event.target.value); }); }} />
            </div>
          ) : null}
          <Field label={t("batches.start.segmentType")}>
            {(control) => (
              <input {...control} className={FORM_CONTROL_CLASS} value={segmentType} placeholder={t("batches.start.segmentTypePlaceholder")} onChange={(event) => { changed(() => { setSegmentType(event.target.value); }); }} />
            )}
          </Field>
          {"problem" in chosen ? <p className={FORM_HELP_TEXT_CLASS}>{t(`batches.start.problem.${chosen.problem}`)}</p> : null}
          <div className="flex items-center gap-2">
            <AppButton variant="secondary" size="sm" disabled={busy || !("scope" in chosen)} onClick={showPreview}>{t("batches.start.preview")}</AppButton>
            {preview === null ? null : (
              <span className="app-text-muted text-xs">
                {t("batches.start.units", { count: preview.unitCount })}:{" "}
                {preview.units.slice(0, PREVIEW_SHOWN).map((unit) => (unit.segmentPath === null ? unit.documentKey : `${unit.documentKey} #${unit.segmentPath}`)).join(", ")}
                {preview.unitCount > PREVIEW_SHOWN ? ", …" : ""}
              </span>
            )}
          </div>
          {previewing.state.status === "failed" ? <ErrorNotice error={previewing.state.error} compact={true} /> : null}
        </FieldGroup>
        <Field label={t("batches.start.batchTitle")}>
          {(control) => (
            <input {...control} className={FORM_CONTROL_CLASS} value={title} maxLength={200} onChange={(event) => { setTitle(event.target.value); replan(); }} />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t("batches.start.concurrency")}>
            {(control) => (
              <input {...control} className={FORM_CONTROL_CLASS} type="number" min={1} value={concurrency} placeholder={t("batches.start.concurrencyDefault")} onChange={(event) => { setConcurrency(event.target.value); replan(); }} />
            )}
          </Field>
          <Field label={t("batches.start.humanSteps")}>
            {(control) => (
              <select {...control} className={FORM_SELECT_CLASS} value={humanSteps} onChange={(event) => { setHumanSteps(event.target.value as HumanStepHandling); replan(); }}>
                <option value="queue">{t("batches.humanSteps.queue")}</option>
                <option value="skip">{t("batches.humanSteps.skip")}</option>
              </select>
            )}
          </Field>
          <div>
            <Field label={t("batches.start.changeSets")}>
              {(control) => (
                <select {...control} className={FORM_SELECT_CLASS} value={grouping} onChange={(event) => { setGrouping(event.target.value as Grouping); replan(); }}>
                  <option value="per_item">{t("batches.start.perItem")}</option>
                  <option value="chunked">{t("batches.start.chunked")}</option>
                </select>
              )}
            </Field>
            {grouping === "chunked" ? (
              <input className={`${FORM_CONTROL_CLASS} mt-1`} type="number" min={1} value={chunkSize} aria-label={t("batches.start.chunkSize")} onChange={(event) => { setChunkSize(event.target.value); replan(); }} />
            ) : null}
          </div>
        </div>
        <FieldGroup legend={t("batches.start.route")} className="space-y-1">
          <p className={FORM_HELP_TEXT_CLASS}>{t("batches.start.routeHint")}</p>
          {llmChoices.status === "loaded" ? (
            <LlmPicker choices={llmChoices.value} onRefresh={refresh} value={routeChoice === null ? null : { connectorId: source.connectorId, route: routeChoice }} onChoose={(choice) => { setChosenRoute(choice.route); replan(); }} />
          ) : (
            <LoadStatus state={llmChoices} loadingLabel={t("threads.llm.loading")} compact />
          )}
        </FieldGroup>
        <p className={FORM_HELP_TEXT_CLASS}>{t("batches.start.connectorHint")}</p>
        {starting.state.status === "failed" ? <ErrorNotice error={starting.state.error} /> : null}
        <div className="flex justify-end gap-2 pt-2">
          <AppButton variant="secondary" size="sm" onClick={onClose} disabled={busy}>{t("common.cancel")}</AppButton>
          <AppButton variant="primary" size="sm" onClick={start} disabled={busy || !("scope" in chosen) || !settingsValid}>
            {starting.state.status === "submitting" ? t("batches.start.starting") : t("batches.start.submit")}
          </AppButton>
        </div>
      </div>
    </ModalShell>
  );
}
